import React, { useEffect, useRef } from 'react';
import { AnimatedGradient } from './ui/animated-gradient';
import GridBackgroundDemo from './ui/grid-background-demo';
import DashboardTiles from './DashboardTiles';
import { APP_URL } from '../lib/config';

const Hero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 2.0; // Set to 2x speed
    }
  }, []);


  return (
    <section className="relative py-12 lg:py-24 overflow-hidden">

      <AnimatedGradient />
      <GridBackgroundDemo />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Logo Mark */}
          <div className="mb-8 flex justify-center">
            <div className="relative group">
              <div className="w-24 h-24 bg-gradient-to-br from-[#0E666F] to-[#0A4A52] rounded-3xl flex items-center justify-center shadow-2xl transform rotate-2 group-hover:rotate-0 transition-all duration-500 group-hover:scale-110 border-2 border-white/20">
                <img 
                  src="/assets/images/convalytics-logo-mark.svg" 
                  alt="ConvaLytics AI" 
                  className="w-12 h-12 filter brightness-0 invert"
                />
              </div>
              {/* Animated AI indicators */}
              <div className="absolute -top-3 -right-3 w-6 h-6 bg-gradient-to-r from-cyan-400 to-teal-500 rounded-full animate-pulse shadow-lg">
                <div className="w-full h-full bg-white/20 rounded-full animate-ping"></div>
              </div>
              <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-full animate-bounce shadow-lg">
                <div className="w-full h-full bg-white/30 rounded-full"></div>
              </div>
              <div className="absolute top-1/2 -right-4 w-3 h-3 bg-gradient-to-r from-cyan-300 to-teal-400 rounded-full animate-pulse shadow-lg"></div>
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0E666F] to-[#0A4A52] rounded-3xl opacity-0 group-hover:opacity-30 blur-2xl transition-opacity duration-500"></div>
              {/* Subtle border glow */}
              <div className="absolute inset-0 rounded-3xl border-2 border-white/10 group-hover:border-white/30 transition-all duration-500"></div>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 font-heading">
            Turn calls into{' '}
            <span className="text-brand-teal">AI-powered insights.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            We Help You Analyze 100% of Your Calls, So You Never Miss a Thing.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a 
              href={APP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-[#0E666F] to-[#0A4A52] hover:from-[#0A4A52] hover:to-[#0E666F] text-white font-semibold text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Get Started
            </a>
            <a 
              href="#contact-form" 
              className="bg-white/90 backdrop-blur-sm hover:bg-white text-[#0E666F] font-semibold text-lg px-8 py-4 rounded-xl border-2 border-[#0E666F]/20 hover:border-[#0E666F]/40 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-block"
            >
              Contact Us
            </a>
          </div>

          {/* Dashboard Tiles */}
          <DashboardTiles />
          
          {/* Full Width Dashboard Tiles */}
          {/* <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 mb-8 mx-8">
              <img 
                src="/assets/images/dashboard/full-tile-1.png" 
                alt="Full Dashboard Tile 1" 
                className="w-full h-auto rounded-2xl"
              />
          </div> */}
          
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 mb-6 mx-8">
              <video 
                ref={videoRef}
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/assets/images/dashboard/Transcription_Thumbnail.png"
                className="w-full h-auto rounded-2xl object-cover"
                style={{ 
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
                onError={(e) => {
                  console.error('Video failed to load:', e);
                }}
                onLoadStart={() => {
                  console.log('Video started loading');
                }}
                onCanPlay={() => {
                  console.log('Video can play');
                }}
              >
                <source src="/assets/images/dashboard/Call_Transcriptions.mov" type="video/quicktime" />
                <source src="/assets/images/dashboard/Call_Transcriptions.mp4" type="video/mp4" />
                <img 
                  src="/assets/images/dashboard/full-tile-2.png" 
                  alt="Call Transcriptions Fallback" 
                  className="w-full h-auto rounded-2xl object-cover"
                  style={{ 
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
                Your browser does not support the video tag.
              </video>
          </div>

          {/* Trusted by section */}
          {/* <div className="mt-16">
            <p className="text-gray-500 mb-8">Adopted by renowned, trusted, and leading enterprises</p>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              {['Enterprise A', 'Company B', 'Corp C', 'Business D', 'Firm E'].map((company, index) => (
                <div key={index} className="text-gray-400 font-semibold">
                  {company}
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default Hero;
